import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import FormToggle from '@shared/atoms/FormFields/FormToggle';
import FormInput from '@shared/atoms/FormFields/FormInput';
import Input from '@shared/atoms/Input';
import LanguageSelect from '@shared/molecules/LanguageSelect';
import T from '@shared/atoms/Typography';
import theme from '@shared/themes/default';
import { useForm } from '@shared/utils';
import { courseStatus, DOMAIN_LOCALES, ORGANIZATION_TYPE } from '@shared/const';

import { validateVideocationCatalogRemoval } from '../../graphql/queries';
import { updateOrganization, updateProduct } from '../../graphql/mutations';
import { useAppGlobals } from '../../../../contexts/AppContext';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
`;

const LanguageWrapper = styled.div`
  align-items: center;
  display: flex;
  column-gap: ${theme.spacing.m1};
`;

const WrapText = styled.div`
  display: flex;
  gap: ${theme.spacing.m1};
  align-items: center;
`;

const SettingsTab = ({
  item,
  onUpdateOrganization,
  setDetailedErrors,
  setErrorMessage,
  setHasError,
  setHasSuccess,
  setSuccessMessage,
}) => {
  const [blockHideVcCourses, setBlockHideVcCourses] = useState(false);

  const { t } = useTranslation();
  const {
    domain,
    useGraphQLMutation,
    useLazyGraphQLQuery,
    videocationCatalog,
  } = useAppGlobals();
  const languages = DOMAIN_LOCALES[domain];

  const showHideVcCourses =
    item.type === ORGANIZATION_TYPE.ENTERPRISE &&
    item.subscription?.product?.catalogs?.length;

  const getDefaultValues = (organization) => ({
    name: organization.name || '',
    hidePriceList: !!organization.hidePriceList,
    hideVcCourses:
      showHideVcCourses &&
      !organization.subscription.product.catalogs.some(
        (catalog) => catalog.id === videocationCatalog.id
      ),
    languageSelect: organization?.invitationLanguage?.code || languages[0],
  });

  const buildDetailedErrors = (
    hasPlaylistsWithVideocationCourses,
    hasVideocationCoursesFeatured,
    hasFeaturedCourse
  ) => {
    const detailedErrors = [];

    if (hasPlaylistsWithVideocationCourses) {
      detailedErrors.push({
        title: t(
          'backoffice.organizations.settings.failedHideVcCoursesBanner.playlistsTitle'
        ),
        description: t(
          'backoffice.organizations.settings.failedHideVcCoursesBanner.playlistsDescription'
        ),
      });
    }
    if (hasVideocationCoursesFeatured) {
      detailedErrors.push({
        title: t(
          'backoffice.organizations.settings.failedHideVcCoursesBanner.vcCourseFeaturedTitle'
        ),
        description: t(
          'backoffice.organizations.settings.failedHideVcCoursesBanner.vcCourseFeaturedDescription'
        ),
      });
    }
    if (!hasFeaturedCourse) {
      detailedErrors.push({
        title: t(
          'backoffice.organizations.settings.failedHideVcCoursesBanner.featuredItemMissingTitle'
        ),
        description: t(
          'backoffice.organizations.settings.failedHideVcCoursesBanner.featuredItemMissingDescription'
        ),
      });
    }
    return detailedErrors;
  };

  const [updateOrganizationMutation, updateOrganizationMutationState] =
    useGraphQLMutation(updateOrganization, {
      update(cache, { data: { updateOrganization } }) {
        onUpdateOrganization(cache, updateOrganization);
        setSuccessMessage(
          t('backoffice.organizations.settings.successfullyUpdated')
        );
        setHasSuccess(true);
      },
      onError() {
        setErrorMessage(t('backoffice.organizations.settings.failedToUpdate'));
        setHasError(true);
      },
    });
  const [updateProductMutation, updateProductMutationState] =
    useGraphQLMutation(updateProduct, {
      update(cache) {
        cache.evict({
          fieldName: 'listCourses',
          args: {
            filter: {
              status: courseStatus.PUBLISHED,
            },
            organizationId: item.id,
          },
        });
        cache.gc();
      },
      onComplete() {
        setSuccessMessage(
          t('backoffice.organizations.settings.successfullyUpdated')
        );
        setHasSuccess(true);
      },
      onError() {
        setErrorMessage(t('backoffice.organizations.settings.failedToUpdate'));
        setHasError(true);
      },
    });
  const [
    validateVideocationCatalogRemovalQuery,
    validateVideocationCatalogRemovalResponse,
  ] = useLazyGraphQLQuery(validateVideocationCatalogRemoval, {
    fetchPolicy: 'network-only',
  });

  const {
    disableSubmit,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    properties,
    setDefaults,
    submitting,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      name: {
        default: '',
        required: true,
      },
      hidePriceList: {
        default: false,
      },
      hideVcCourses: {},
      languageSelect: {
        // default language is always the first element
        default: languages[0],
      },
    },
    onSubmitHook: async (organization) => {
      const {
        languageSelect,
        hideVcCourses,
        hidePriceList,
        ...newOrganizationData
      } = organization;
      const defaults = getDefaultValues(item);

      if (hideVcCourses && !defaults.hideVcCourses) {
        const validationResponse = await validateVideocationCatalogRemovalQuery(
          {
            variables: { organizationId: item.id },
          }
        );
        const {
          allowed,
          hasPlaylistsWithVideocationCourses,
          hasVideocationCoursesFeatured,
          hasFeaturedCourse,
        } = validationResponse?.data?.validateVideocationCatalogRemoval || {};
        if (!allowed) {
          setErrorMessage(
            t(
              'backoffice.organizations.settings.failedHideVcCoursesBannerTitle'
            )
          );
          const detailedErrors = buildDetailedErrors(
            hasPlaylistsWithVideocationCourses,
            hasVideocationCoursesFeatured,
            hasFeaturedCourse
          );
          setDetailedErrors(detailedErrors);
          setHasError(true);
          setDefaults(getDefaultValues(item));
          setBlockHideVcCourses(true);
          return;
        }
      }

      newOrganizationData.invitationLanguage = languageSelect;
      newOrganizationData.hidePriceList = hidePriceList;
      updateOrganizationMutation({
        variables: {
          input: {
            id: item.id,
            ...newOrganizationData,
          },
        },
      });
      if (showHideVcCourses && hideVcCourses !== defaults.hideVcCourses) {
        updateProductMutation({
          variables: {
            input: {
              id: item.subscription.product.id,
              organizationId: item.id,
              hasVideocationCatalog: !hideVcCourses,
            },
          },
        });
      }
    },
    onSubmitHookEnded:
      !updateOrganizationMutationState.loading &&
      !updateProductMutationState.loading &&
      !validateVideocationCatalogRemovalResponse.loading,
  });

  useEffect(() => {
    setDefaults(getDefaultValues(item));
  }, [item]);

  return (
    <FormWrapper>
      <FormInput
        id="name"
        name="name"
        label={t('backoffice.organizations.settings.organizationName')}
        theme="dark"
        values={values}
        handleChange={handleChange}
        handleBlur={handleBlur}
        properties={properties}
        errors={errors}
        placeholder="Enter organization name"
        sx={{ width: '30%' }}
      />
      <FormToggle
        id="hidePriceList"
        label={t('backoffice.organizations.settings.hidePriceList')}
        name="hidePriceList"
        handleChange={handleChange}
        properties={properties}
        values={values}
      />
      {showHideVcCourses && (
        <WrapText>
          <FormToggle
            id="hideVcCourses"
            label={t('backoffice.organizations.settings.hideVcCourses')}
            name="hideVcCourses"
            handleChange={handleChange}
            properties={properties}
            values={values}
            disabled={blockHideVcCourses}
          />
          {blockHideVcCourses && (
            <T color={theme.palette.errorDark} variant="paragraphS">
              {t(`backoffice.organizations.settings.failedHideVcCoursesInline`)}
            </T>
          )}
        </WrapText>
      )}
      <LanguageWrapper>
        <LanguageSelect
          id="language"
          languages={languages}
          onChange={handleChange}
          theme="dark"
          value={values.languageSelect}
        />
        <T
          color={theme.palette.lightPure}
          fontWeight="medium"
          variant="paragraph"
        >
          {t('backoffice.organizations.settings.invitationLanguage')}
        </T>
      </LanguageWrapper>
      <T>{t('backoffice.organizations.settings.organizationType')}</T>
      <Input
        id="type"
        name="type"
        theme="dark"
        disabled
        value={item.type}
        sx={{ width: '10%' }}
      />
      <T>{t('backoffice.organizations.settings.hubspotCompanyId')}</T>
      <Input
        id="hubspotCompanyId"
        name="hubspotCompanyId"
        properties={properties}
        theme="dark"
        disabled
        value={item.hubspotCompanyId || item.hubspotSyncedId || ''}
        sx={{ width: '10%' }}
      />
      <Button
        ariaLabel={t('generic.save')}
        disabled={disableSubmit}
        loading={submitting}
        loadingOnIcon={submitting}
        onClick={handleSubmit}
        sx={{ alignSelf: 'end' }}
        theme="dark"
      >
        {t('generic.save')}
      </Button>
    </FormWrapper>
  );
};

SettingsTab.propTypes = {
  item: PropTypes.object.isRequired,
  onUpdateOrganization: PropTypes.func.isRequired,
  setDetailedErrors: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setHasError: PropTypes.func.isRequired,
  setHasSuccess: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

export default SettingsTab;
